import { forwardRef, type MouseEvent } from "react"
import type { Keys } from "react-hotkeys-hook"

import type { SX, ThemeKeys } from "../../theme"
import { Button } from "../Button"
import { Icon } from "../Icon/Icon"
import type { IconName } from "../Icon/iconNames"

export interface IconButtonProps {
  color?: Color
  hasPadding?: boolean
  hiddenLabel?: string
  icon: IconName | JSX.Element
  loading?: boolean
  hotkeys?: Keys
  radius?: "normal" | "full"
  size?: "small" | "medium" | "large"
  sx?: SX
  variant?: "solid" | "ghost"
  disabled?: boolean

  /**
   * The type of HTML button to render. Defaults to "button".
   * You may want to use "submit" for your primary form button.
   */
  type?: "button" | "submit" | "reset"
  /**
   * The form ID this button is associated with.
   */
  form?: string

  onClick?: (event: MouseEvent | KeyboardEvent) => void
  onMouseDown?: (event: MouseEvent) => void
  onMouseUp?: (event: MouseEvent) => void
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(props, ref) {
    const {
      color = "grey",
      hasPadding = true,
      icon,
      loading = false,
      radius = "normal",
      size = "medium",
      variant = "ghost",
      disabled = false,
      onClick,
      onMouseDown,
      onMouseUp,
      hiddenLabel,
      sx,
      type = "button",
      ...implicitProps
    } = props

    const processedIcon =
      typeof icon === "string" ? (
        <Icon
          name={icon}
          size={size}
          color={variant === "ghost" ? colorMap[color] : solidColorMap[color]}
        />
      ) : (
        icon
      )

    return (
      <Button
        {...implicitProps}
        type={type}
        ref={ref}
        renderStartIcon={() => processedIcon}
        color={color}
        disabled={disabled || loading}
        loading={loading}
        size={size}
        radius={radius}
        invisible={variant === "ghost"}
        hasPadding={hasPadding}
        hiddenLabel={hiddenLabel}
        onClick={onClick}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        sx={sx}
      />
    )
  },
)

type Color =
  | "grey"
  | "darkGrey"
  | "green"
  | "amber"
  | "purple"
  | "tomato"
  | "indigo"

type ColorMap = Record<Color, ThemeKeys<"color">>

const colorMap: ColorMap = {
  grey: "grey11",
  darkGrey: "grey11",
  green: "green11",
  amber: "amber11",
  purple: "purple11",
  tomato: "tomato11",
  indigo: "indigo11",
}

const solidColorMap: ColorMap = {
  grey: "grey11",
  darkGrey: "white",
  green: "grey2",
  amber: "grey2",
  purple: "grey2",
  tomato: "grey2",
  indigo: "grey2",
}
